import React, { useEffect, useRef } from 'react';
import { FaSeedling, FaTree, FaAppleAlt, FaMedkit, FaLeaf, FaProjectDiagram } from 'react-icons/fa';
import './farmer-based-project.css';

const FarmerBasedProject = () => {
    const scrollRefs = useRef([]);

    const addToRefs = (el) => {
        if (el && !scrollRefs.current.includes(el)) {
            scrollRefs.current.push(el);
        }
    };

    useEffect(() => {
        window.scrollTo(0, 0);
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('visible');
                    }
                });
            },
            { threshold: 0.15 }
        );

        scrollRefs.current.forEach((ref) => {
            if (ref) observer.observe(ref);
        });

        return () => {
            scrollRefs.current.forEach((ref) => {
                if (ref) observer.unobserve(ref);
            });
        };
    }, []);

    const projectFeatures = [
        {
            id: 1,
            title: "Field Crops Integration",
            icon: <FaSeedling />,
            desc: "Laying the foundation for stable, consistent seasonal yields to support the farmer's base income while longer-term crops mature.",
            image: "https://res.cloudinary.com/dexueorjm/image/upload/v1772688736/PHOTO-2026-03-02-10-12-14_v7qxqm.jpg"
        },
        {
            id: 2,
            title: "Exotic Fruits Cultivation",
            icon: <FaAppleAlt />,
            desc: "Introducing high-value avocado and dragon fruit varietals to dramatically increase the profitability and export potential of the farmland.",
            image: "https://images.unsplash.com/photo-1523049673857-eb18f1d7b578?q=80&w=800&auto=format&fit=crop"
        },
        {
            id: 3,
            title: "Medicinal & Aromatic Plants",
            icon: <FaMedkit />,
            desc: "Strategic intercropping with Ashwagandha, Lavender, and other high-demand botanicals to maximize space and soil utility without competing for nutrients.",
            image: "https://res.cloudinary.com/dexueorjm/image/upload/v1772688733/PHOTO-2026-03-02-09-52-47_2_juyti7.jpg"
        },
        {
            id: 4,
            title: "Forestry Wood Bearings",
            icon: <FaTree />,
            desc: "Long-term investment planting with premium timber such as sandalwood and mahogany to serve as a high-return retirement crop for the farmer.",
            image: "https://res.cloudinary.com/dexueorjm/image/upload/v1772688732/PHOTO-2026-03-02-10-05-25_4_flb6zr.jpg"
        }
    ];

    return (
        <div className="fbp-page">
            {/* Hero Section */}
            <section className="fbp-hero">
                <div className="fbp-hero-overlay"></div>
                <div className="fbp-hero-content" ref={addToRefs}>
                    <span className="fbp-badge">Amaravathi Naturo Tech Pvt Ltd</span>
                    <h1 className="fbp-title">Farmer Based Multi-Cropping</h1>
                    <p className="fbp-subtitle">
                        A revolutionary approach to land management. We empower farmers to maximize their acreage through intelligent, sustainable, and high-yield multi-cropping systems.
                    </p>
                </div>
            </section>

            {/* Main Vision Section */}
            <section className="fbp-vision-section">
                <div className="fbp-container">
                    <div className="fbp-vision-grid">
                        <div className="fbp-vision-text" ref={addToRefs}>
                            <h2>The MCP & MCFP Advantage</h2>
                            <p>
                                The Multi Cropping Project (MCP) and Multi Cropping Farmland Project (MCFP) are built on the foundational belief that <strong>no land should be wasted</strong>.
                                <br /><br />
                                By combining fast-yielding field crops with medium-term exotices and long-term forestry, we create a staggered income model. This drastically reduces the farmer's financial risk, rebuilds the soil's natural biome, and guarantees year-round ecological productivity.
                            </p>
                            <div className="fbp-highlight-box">
                                <FaProjectDiagram className="highlight-icon" />
                                <div>
                                    <h4>100% Land Utility</h4>
                                    <p>Every square foot is optimized for complementary root depths and sunlight needs.</p>
                                </div>
                            </div>
                        </div>
                        <div className="fbp-vision-image-wrapper" ref={addToRefs}>
                            <img
                                src="https://res.cloudinary.com/dexueorjm/image/upload/v1772688737/PHOTO-2026-03-02-09-50-14_2_fkuot0.jpg"
                                alt="Multi Cropping Fields"
                                className="fbp-vision-img"
                            />
                            <div className="fbp-img-decoration"></div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Implementation Pillars */}
            <section className="fbp-pillars-section">
                <div className="fbp-container">
                    <div className="fbp-section-header" ref={addToRefs}>
                        <span className="sub-heading">Our Blueprint</span>
                        <h2>Integrated Crop Architecture</h2>
                        <div className="heading-divider"></div>
                    </div>

                    <div className="fbp-pillars-grid">
                        {projectFeatures.map((feature, index) => (
                            <div className="fbp-pillar-card" key={feature.id} ref={addToRefs} style={{ transitionDelay: `${index * 0.15}s` }}>
                                <div className="pillar-image-container">
                                    <img src={feature.image} alt={feature.title} />
                                    <div className="pillar-icon-badge">
                                        {feature.icon}
                                    </div>
                                </div>
                                <div className="pillar-content">
                                    <h3>{feature.title}</h3>
                                    <p>{feature.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Empowerment Banner */}
            <section className="fbp-empowerment-banner" ref={addToRefs}>
                <div className="fbp-container text-center">
                    {/* <FaLeaf className="banner-icon" /> */}
                    <h2>Empowering Farmers, Enriching the Earth</h2>
                    {/* <p>We provide full-cycle support from soil analysis and planting to global market distribution.</p>
                    <button className="fbp-cta-btn">Join the Project</button> */}
                </div>
            </section>
        </div>
    );
};

export default FarmerBasedProject;
